import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Camera, Loader2, Search, Sparkles, Trash2, Plus, ExternalLink,
  AlertTriangle, CheckCircle2, RotateCcw, PackagePlus, ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIDENCE_TONE = {
  high: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-50 text-amber-900 border-amber-200',
  low: 'bg-red-50 text-red-800 border-red-200',
  manual: 'bg-slate-100 text-slate-700 border-slate-200',
};

/**
 * Photograph a rating plate, get a catalogue entry.
 *
 * Deliberately a review step rather than a one-tap save: the plate read is
 * good but not infallible, and a wrong spec sheet in the register is worse
 * than no spec sheet. Everything the machine produced is editable here, and
 * the plate photo stays attached as the evidence behind it.
 */
// What has to be done to the thing periodically. Driven off the equipment
// class the scan works out, but always the reviewer's call — a torque wrench
// that gets PAT tested and never calibrated is one nobody can trust.
const REGIMES = ['pat', 'calibration', 'inspection', 'warranty'];

/**
 * @param mode 'catalogue' (default) registers the tool *type*; 'asset' treats
 *   the scan as the first step of registering the unit in your hand — it
 *   resolves the catalogue entry, then hands the per-unit detail off the
 *   plate (serial, year, the plate photo itself) to `onReadyForAsset` so the
 *   asset form can open pre-filled for review.
 */
const NameplateScanDialog = ({ open, onOpenChange, categories = [], onSaved, onAssetCreated, mode = 'catalogue', onReadyForAsset }) => {
  const { t, i18n } = useTranslation();
  const fileRef = useRef(null);
  const [stage, setStage] = useState('capture'); // capture | working | review
  const [working, setWorking] = useState('');
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [manualQ, setManualQ] = useState({ manufacturer: '', model: '' });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [busyCompanion, setBusyCompanion] = useState(null);
  const [doneCompanions, setDoneCompanions] = useState([]);

  const reset = () => {
    setStage('capture'); setResult(null); setForm(null); setSpecs([]);
    setPreview(null); setManualQ({ manufacturer: '', model: '' });
    setCompanions([]); setDoneCompanions([]);
  };

  const close = (open2) => {
    if (!open2) reset();
    onOpenChange(open2);
  };

  const adoptDraft = (data) => {
    const d = data.draft || {};
    setResult(data);
    setSpecs(d.specs || []);
    setForm({
      name: d.name || '',
      manufacturer: d.manufacturer || '',
      part_number: d.part_number || '',
      serial_number: d.serial_number || '',
      year_of_manufacture: d.year_of_manufacture || '',
      manual_link: d.manual_link || '',
      category_id: d.suggested_category?.id || '',
      asset_type: d.asset_type || 'tangible',
      pat_required: !!d.pat_required,
      compliance_regime: d.compliance_regime || 'inspection',
      equipment_class: d.equipment_class || 'other',
      inspection_interval_months: d.suggested_interval_months ?? '',
      useful_life_years: '',
      notes: d.notes || '',
    });
    setStage('review');
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error(t('inv.nameplate.wrongFormat'));
      return;
    }
    setPreview(URL.createObjectURL(file));
    setStage('working');
    setWorking('reading');

    const body = new FormData();
    body.append('file', file);
    body.append('lookup', 'true');
    body.append('lang', i18n.language === 'pl' ? 'pl' : 'en');
    try {
      const r = await axios.post(`${API}/inventory/nameplate/scan`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!r.data.readable) {
        toast.error(r.data.message || t('inv.nameplate.unreadable'));
        setStage('capture');
        return;
      }
      adoptDraft(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.nameplate.scanFailed'));
      setStage('capture');
    }
  };

  const runManualLookup = async () => {
    if (!manualQ.model.trim()) { toast.error(t('inv.nameplate.modelRequired')); return; }
    setStage('working');
    setWorking('searching');
    try {
      const r = await axios.post(`${API}/inventory/nameplate/lookup`, {
        manufacturer: manualQ.manufacturer.trim() || null,
        model: manualQ.model.trim(),
        lang: i18n.language === 'pl' ? 'pl' : 'en',
      });
      adoptDraft(r.data);
      if (!r.data.web_found) toast.info(t('inv.nameplate.noPageFound'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.nameplate.lookupFailed'));
      setStage('capture');
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const payload = () => ({
    name: form.name.trim(),
    manufacturer: form.manufacturer.trim() || null,
    category_id: form.category_id || null,
    asset_type: form.asset_type,
    part_number: form.part_number.trim() || null,
    manual_link: form.manual_link.trim() || null,
    pat_required: form.pat_required,
    inspection_interval_months: form.inspection_interval_months ? Number(form.inspection_interval_months) : null,
    useful_life_years: form.useful_life_years ? Number(form.useful_life_years) : null,
    notes: form.notes || null,
    equipment_class: form.equipment_class || null,
    compliance_regime: form.compliance_regime || null,
    specs: specs.filter((s) => s.label && s.value),
    source_url: result?.draft?.source_url || null,
    plate_photo_gridfs_id: result?.draft?.plate_photo_gridfs_id || null,
    year_of_manufacture: form.year_of_manufacture || null,
  });

  /** Pull the product photo we found into the catalogue entry. A failure here
   *  never fails the save — but the user is told, so they know to add one. */
  const adoptImage = async (modelId, imageUrl) => {
    const url = imageUrl || result?.draft?.image_url;
    if (!url) return;
    try {
      await axios.post(`${API}/inventory/nameplate/models/${modelId}/image-from-url`, { image_url: url });
    } catch (err) {
      toast.warning(t('inv.nameplate.photoFailed'), {
        description: err.response?.data?.detail || err.message,
      });
    }
  };

  /** Save the catalogue entry, then either register this unit too, or move
   *  on to the charger and batteries if the page listed any. */
  const saveNew = async (alsoRegisterUnit) => {
    if (!form.name.trim()) { toast.error(t('inv.nameplate.nameRequired')); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/inventory/asset-models`, payload());
      await adoptImage(r.data.id);
      const model = { ...r.data, scanned_serial: form.serial_number || null };

      if (alsoRegisterUnit) {
        const params = form.serial_number
          ? `?serial_number=${encodeURIComponent(form.serial_number)}` : '';
        const asset = await axios.post(`${API}/inventory/assets/from-model/${r.data.id}${params}`);
        toast.success(t('inv.nameplate.assetRegistered', { code: asset.data.inventory_number }));
        onAssetCreated?.(asset.data);
      } else {
        toast.success(t('inv.nameplate.savedToCatalogue', { name: r.data.name }));
      }

      onSaved?.(model);
      const pending = result?.draft?.companions || [];
      if (pending.length) { setCompanions(pending); setStage('companions'); }
      else close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.common.saveFailed'));
    }
    setSaving(false);
  };

  /** Register a charger or battery pack listed alongside the tool. Looks it
   *  up in its own right so it gets its own specs, not the parent's. */
  const addCompanion = async (c) => {
    setBusyCompanion(c.model);
    try {
      const lk = await axios.post(`${API}/inventory/nameplate/lookup`, {
        manufacturer: form.manufacturer || null,
        model: c.model,
        lang: i18n.language === 'pl' ? 'pl' : 'en',
      });
      const d = lk.data.draft || {};
      const r = await axios.post(`${API}/inventory/asset-models`, {
        name: d.name || c.model,
        manufacturer: d.manufacturer || form.manufacturer || null,
        category_id: d.suggested_category?.id || null,
        asset_type: 'tangible',
        part_number: d.part_number || null,
        manual_link: d.manual_link || null,
        pat_required: !!d.pat_required,
        inspection_interval_months: d.suggested_interval_months ?? null,
        equipment_class: d.equipment_class || (c.kind === 'charger' ? 'charger' : 'battery_pack'),
        compliance_regime: d.compliance_regime || (c.kind === 'charger' ? 'pat' : 'inspection'),
        specs: d.specs || [],
        source_url: d.source_url || null,
      });
      if (d.image_url) await adoptImage(r.data.id, d.image_url);
      toast.success(t('inv.nameplate.companionAdded', { name: r.data.name }));
      setDoneCompanions((p) => [...p, c.model]);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.nameplate.companionFailed'));
    }
    setBusyCompanion(null);
  };

  const updateExisting = async () => {
    const dup = result?.draft?.duplicate;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/inventory/nameplate/models/${dup.id}/enrich`, {
        specs: specs.filter((s) => s.label && s.value),
        part_number: form.part_number.trim() || null,
        manual_link: form.manual_link.trim() || null,
        source_url: result?.draft?.source_url || null,
        plate_photo_gridfs_id: result?.draft?.plate_photo_gridfs_id || null,
      });
      if (!dup.default_picture_gridfs_id) await adoptImage(dup.id);
      toast.success(r.data.updated
        ? t('inv.nameplate.enriched', { count: r.data.added.length })
        : t('inv.nameplate.nothingNew'));
      onSaved?.({ ...(r.data.model || dup), scanned_serial: form.serial_number || null });
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.common.saveFailed'));
    }
    setSaving(false);
  };

  const useExisting = () => {
    const dup = result?.draft?.duplicate;
    onSaved?.({ ...dup, scanned_serial: form.serial_number || null });
    close(false);
  };

  /** The per-unit facts a catalogue entry can never hold: this exact tool's
   *  serial, the year it left the factory, and the photo of its own plate.
   *
   *  `reviewed` carries what the user just confirmed on screen. When we reuse
   *  an older catalogue entry that happens to have a blank manufacturer or
   *  category, these fill the gap rather than leaving the asset blank too. */
  const plateExtras = () => ({
    serial_number: form.serial_number?.trim() || null,
    year_of_manufacture: form.year_of_manufacture?.trim() || null,
    plate_photo_gridfs_id: result?.draft?.plate_photo_gridfs_id || null,
    reviewed: {
      manufacturer: form.manufacturer?.trim() || null,
      category_id: form.category_id || null,
      part_number: form.part_number?.trim() || null,
      manual_link: form.manual_link?.trim() || null,
      inspection_interval_months: form.inspection_interval_months || null,
      pat_required: !!form.pat_required,
    },
  });

  /** Asset mode: make sure the tool type exists in the catalogue (create it,
   *  or reuse the one we matched), then hand over for review. */
  const continueToAsset = async () => {
    if (!form.name.trim()) { toast.error(t('inv.nameplate.nameRequired')); return; }
    const existing = result?.draft?.duplicate;
    setSaving(true);
    try {
      let model = existing;
      if (!model) {
        const r = await axios.post(`${API}/inventory/asset-models`, payload());
        await adoptImage(r.data.id);
        model = r.data;
        toast.success(t('inv.nameplate.savedToCatalogue', { name: model.name }));
      }
      onSaved?.(model);
      onReadyForAsset?.({ model, plate: plateExtras() });
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.common.saveFailed'));
    }
    setSaving(false);
  };

  const dup = result?.draft?.duplicate;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="nameplate-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" /> {t('inv.nameplate.title')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'asset' ? t('inv.nameplate.assetSubtitle') : t('inv.nameplate.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {stage === 'capture' && (
          <div className="space-y-4">
            <button type="button" onClick={() => fileRef.current?.click()}
              className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 transition-colors hover:border-[#AA0000] hover:bg-red-50/40"
              data-testid="nameplate-capture-btn">
              <Camera className="h-9 w-9 text-[#AA0000]" />
              <span className="text-sm font-semibold">{t('inv.nameplate.takePhoto')}</span>
              <span className="max-w-sm text-center text-xs text-muted-foreground">
                {t('inv.nameplate.photoHint')}
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
              capture="environment" className="hidden" onChange={onPickFile}
              data-testid="nameplate-file-input" />

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('inv.nameplate.orType')}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.3fr_auto]">
              <Input value={manualQ.manufacturer} placeholder={t('inv.assets.manufacturer')}
                onChange={(e) => setManualQ((p) => ({ ...p, manufacturer: e.target.value }))}
                data-testid="nameplate-manual-manufacturer" />
              <Input value={manualQ.model} placeholder={t('inv.nameplate.modelPlaceholder')}
                onChange={(e) => setManualQ((p) => ({ ...p, model: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && runManualLookup()}
                data-testid="nameplate-manual-model" />
              <Button onClick={runManualLookup} data-testid="nameplate-manual-lookup-btn">
                <Search className="mr-1 h-4 w-4" /> {t('inv.nameplate.lookUp')}
              </Button>
            </div>
          </div>
        )}

        {stage === 'working' && (
          <div className="flex flex-col items-center gap-3 py-12" data-testid="nameplate-working">
            {preview && (
              <img src={preview} alt="" className="mb-2 max-h-40 rounded-lg border object-contain" />
            )}
            <Loader2 className="h-7 w-7 animate-spin text-[#AA0000]" />
            <p className="text-sm font-medium">
              {t(working === 'reading' ? 'inv.nameplate.readingPlate' : 'inv.nameplate.searching')}
            </p>
            <p className="max-w-sm text-center text-xs text-muted-foreground">
              {t('inv.nameplate.workingHint')}
            </p>
          </div>
        )}

        {stage === 'companions' && (
          <div className="space-y-3" data-testid="nameplate-companions">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-blue-900">
                <PackagePlus className="h-4 w-4" /> {t('inv.nameplate.companionsTitle')}
              </p>
              <p className="mt-0.5 text-xs text-blue-900">{t('inv.nameplate.companionsHint')}</p>
            </div>
            <div className="space-y-1.5">
              {companions.map((c) => {
                const done = doneCompanions.includes(c.model);
                return (
                  <div key={c.model} className="flex items-center justify-between gap-2 rounded-lg border p-2.5"
                    data-testid={`nameplate-companion-${c.model}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium break-words">{c.name || c.model}</p>
                      <p className="text-xs text-muted-foreground">
                        {[t(`inv.nameplate.companionKind.${c.kind}`, { defaultValue: c.kind }), c.model]
                          .filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button size="sm" variant={done ? 'ghost' : 'outline'} disabled={done || !!busyCompanion}
                      onClick={() => addCompanion(c)}
                      data-testid={`nameplate-add-companion-${c.model}`}>
                      {done ? <><CheckCircle2 className="mr-1 h-3.5 w-3.5 text-emerald-600" />{t('inv.nameplate.companionDone')}</>
                        : busyCompanion === c.model ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><Plus className="mr-1 h-3.5 w-3.5" />{t('common.add')}</>}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end border-t pt-3">
              <Button onClick={() => close(false)} data-testid="nameplate-companions-done-btn">
                {t('inv.nameplate.companionsDone')}
              </Button>
            </div>
          </div>
        )}

        {stage === 'review' && form && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={CONFIDENCE_TONE[result?.draft?.confidence] || ''}
                data-testid="nameplate-confidence">
                {t(`inv.nameplate.confidence.${result?.draft?.confidence || 'low'}`)}
              </Badge>
              {result?.web_found ? (
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> {t('inv.nameplate.foundOnSite')}
                </Badge>
              ) : (
                <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                  {t('inv.nameplate.plateOnly')}
                </Badge>
              )}
              {result?.draft?.source_url && (
                <a href={result.draft.source_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  data-testid="nameplate-source-link">
                  <ExternalLink className="h-3 w-3" /> {t('inv.nameplate.viewSource')}
                </a>
              )}
              <Button variant="ghost" size="sm" className="ml-auto" onClick={reset}
                data-testid="nameplate-rescan-btn">
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> {t('inv.nameplate.startAgain')}
              </Button>
            </div>

            {dup && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="nameplate-duplicate">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4" /> {t('inv.nameplate.alreadyHave')}
                </p>
                <p className="mt-0.5 text-xs text-amber-900">
                  {t('inv.nameplate.alreadyHaveHint', {
                    name: [dup.manufacturer, dup.name].filter(Boolean).join(' '),
                  })}
                </p>
                {mode === 'asset' ? (
                  <p className="mt-1 text-xs font-medium text-amber-900" data-testid="nameplate-will-reuse">
                    {t('inv.nameplate.willReuse')}
                  </p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" onClick={useExisting} data-testid="nameplate-use-existing-btn">
                      {t('inv.nameplate.useExisting')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={updateExisting} disabled={saving}
                      data-testid="nameplate-update-existing-btn">
                      <Sparkles className="mr-1 h-3.5 w-3.5" /> {t('inv.nameplate.topUpExisting')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              {(preview || result?.draft?.image_url) && (
                <img src={result?.draft?.image_url || preview} alt=""
                  className="h-24 w-24 shrink-0 rounded-lg border bg-white object-contain" />
              )}
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">{t('inv.models.modelName')} *</Label>
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)}
                    data-testid="nameplate-name-input" />
                </div>
                <div>
                  <Label className="text-xs">{t('inv.assets.manufacturer')}</Label>
                  <Input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)}
                    data-testid="nameplate-manufacturer-input" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <Label className="text-xs">{t('inv.assets.partNumber')}</Label>
                <Input value={form.part_number} onChange={(e) => set('part_number', e.target.value)}
                  data-testid="nameplate-part-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.assets.serialNumber')}</Label>
                <Input value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)}
                  data-testid="nameplate-serial-input" />
                <p className="mt-0.5 text-[11px] text-muted-foreground">{t('inv.nameplate.serialHint')}</p>
              </div>
              <div>
                <Label className="text-xs">{t('inv.nameplate.year')}</Label>
                <Input value={form.year_of_manufacture}
                  onChange={(e) => set('year_of_manufacture', e.target.value)}
                  data-testid="nameplate-year-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <Label className="text-xs">{t('inv.common.category')}</Label>
                <Select value={form.category_id || 'none'}
                  onValueChange={(v) => set('category_id', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="nameplate-category-select">
                    <SelectValue placeholder={t('inv.common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common.none')}</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {result?.draft?.suggested_category && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {t('inv.nameplate.suggestedFrom', { hint: result.draft.category_hint })}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">{t('inv.models.inspectionInterval')}</Label>
                <Input type="number" value={form.inspection_interval_months}
                  onChange={(e) => set('inspection_interval_months', e.target.value)}
                  data-testid="nameplate-interval-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.nameplate.regime')}</Label>
                <Select value={form.compliance_regime}
                  onValueChange={(v) => { set('compliance_regime', v); set('pat_required', v === 'pat'); }}>
                  <SelectTrigger data-testid="nameplate-regime-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIMES.map((r) => (
                      <SelectItem key={r} value={r}>{t(`inv.nameplate.regimes.${r}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {t(`inv.nameplate.regimeHint.${form.compliance_regime}`)}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('inv.form.manualLink')}</Label>
              <Input value={form.manual_link} onChange={(e) => set('manual_link', e.target.value)}
                placeholder={t('auto.inv.https2')} data-testid="nameplate-manual-input" />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="text-xs">
                  {t('inv.nameplate.specs', { count: specs.length })}
                </Label>
                <Button variant="ghost" size="sm"
                  onClick={() => setSpecs([...specs, { label: '', value: '' }])}
                  data-testid="nameplate-add-spec-btn">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t('common.add')}
                </Button>
              </div>
              {specs.length === 0 ? (
                <p className="rounded border border-dashed py-4 text-center text-xs text-muted-foreground">
                  {t('inv.nameplate.noSpecs')}
                </p>
              ) : (
                <div className="max-h-64 space-y-1 overflow-y-auto rounded border p-2">
                  {specs.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`nameplate-spec-row-${i}`}>
                      <Input className="h-8 flex-1 text-xs" value={s.label}
                        placeholder={t('inv.nameplate.specLabel')}
                        onChange={(e) => setSpecs(specs.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                      <Input className="h-8 w-40 text-xs" value={s.value}
                        placeholder={t('inv.nameplate.specValue')}
                        onChange={(e) => setSpecs(specs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-red-500"
                        onClick={() => setSpecs(specs.filter((_, j) => j !== i))}
                        data-testid={`nameplate-remove-spec-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => close(false)} data-testid="nameplate-cancel-btn">
                {t('common.cancel')}
              </Button>
              {mode === 'asset' ? (
                /* The plate is in front of you, so the unit is what you came
                   to register — the catalogue entry is just a prerequisite. */
                <Button onClick={continueToAsset} disabled={saving}
                  data-testid="nameplate-continue-to-asset-btn">
                  {saving ? t('inv.common.saving') : t('inv.nameplate.continueToAsset')}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => saveNew(false)} disabled={saving}
                    data-testid="nameplate-save-btn">
                    {saving ? t('inv.common.saving')
                      : dup ? t('inv.nameplate.saveAnyway') : t('inv.nameplate.addToCatalogue')}
                  </Button>
                  {/* A plate in front of you means you are holding the thing, so
                      registering this actual unit is usually what you want. */}
                  <Button onClick={() => saveNew(true)} disabled={saving}
                    data-testid="nameplate-save-and-register-btn">
                    {t('inv.nameplate.addAndRegister')} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NameplateScanDialog;
